/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HTTPPolicySetModule
 */

import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';

import { IAviCollectionDataGridConfig } from 'ng/modules/data-grid';
import { L10nService } from '@vmw/ngx-vip';

import {
    HTTPPolicySetCollection,
} from 'ajs/modules/http-policy-set/factories/http-policy-set.collection.factory';

import {
    HTTPPolicySetItem,
} from 'ajs/modules/http-policy-set/factories/http-policy-set.item.factory';

import * as globalL10n from 'global-l10n';
import * as l10n from './http-policy-set-list.component.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type THTTPPolicySetCollection = typeof HTTPPolicySetCollection;

/**
 * @description HTTP Policy set list page.
 *
 * @author Rohit Gaikwad
 */
@Component({
    selector: 'http-policy-set-list',
    templateUrl: './http-policy-set-list.component.html',
})
export class HTTPPolicySetListComponent implements OnInit, OnDestroy {
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * HTTPPolicySet collection object.
     */
    public readonly httpPolicySetCollection: HTTPPolicySetCollection;

    /**
     * HTTPPolicySet grid config object.
     */
    public httpPolicySetGridConfig: IAviCollectionDataGridConfig;

    constructor(
        public l10nService: L10nService,
        @Inject(HTTPPolicySetCollection)
        public HTTPPolicySetCollection: THTTPPolicySetCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.httpPolicySetCollection = new HTTPPolicySetCollection();
    }

    /** @override */
    public ngOnInit(): void {
        const { objectName } = this.httpPolicySetCollection;

        this.httpPolicySetGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.httpPolicySetCollection,
            fields: [{
                id: 'name',
                label: this.l10nService.getMessage(globalL10nKeys.nameLabel),
                transform: row => row.getName(),
            }, {
                id: 'totalSecurityRules',
                label: this.l10nService.getMessage(l10nKeys.columnTitleTotalSecurityRules),
                transform: (row: HTTPPolicySetItem): string => {
                    const { config } = row;
                    const securityRuleCount: number = config.http_security_policy?.rules ?
                        config.http_security_policy.rules.count : 0;

                    return securityRuleCount.toString();
                },
            }, {
                id: 'totalRequestRules',
                label: this.l10nService.getMessage(l10nKeys.columnTitleTotalRequestRules),
                transform: (row: HTTPPolicySetItem): string => {
                    const { config } = row;

                    const requestRuleCount: number = config.http_request_policy?.rules ?
                        config.http_request_policy.rules.count : 0;

                    return requestRuleCount.toString();
                },
            }, {
                id: 'totalResponseRules',
                label: this.l10nService.getMessage(l10nKeys.columnTitleTotalResponseRules),
                transform: (row: HTTPPolicySetItem): string => {
                    const { config } = row;

                    const responseRuleCount: number = config.http_response_policy?.rules ?
                        config.http_response_policy.rules.count : 0;

                    return responseRuleCount.toString();
                },
            }],
            layout: {
                placeholderMessage: this.l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
            },
        };
    }

    /** @override */
    public ngOnDestroy(): void {
        this.httpPolicySetCollection.destroy();
    }
}
